import type { GridApi } from "ag-grid-community";
import type { Ref } from "vue";

import { draggedIdFromTypes, isFromDragGroup } from "../../../dragAndDrop/dragTransfer";
import { dropIndicatorStyles } from "../../../dragAndDrop/rowIndicatorClass";
import { CHIP_SELECTOR, OVERFLOWED_CHIP_CLASS } from "./useRowGroupPanelOverflow";

const INDICATOR_CLASSES = [dropIndicatorStyles.indicatorBefore, dropIndicatorStyles.indicatorAfter];
const CHIPS_BOX_SELECTOR = ".ag-column-drop-horizontal";

const isOverChips = (target: EventTarget | null) => target instanceof Element && !!target.closest(CHIPS_BOX_SELECTOR);

/**
 * The `moveRowGroupColumn` arguments for dropping `draggedId` into `slot` (0 = before the first
 * column); null when that leaves the order unchanged. `draggedId` comes off the drag's type list,
 * which browsers lowercase, so it is matched case-insensitively.
 */
export function rowGroupDropMove(
  colIds: readonly string[],
  draggedId: string,
  slot: number,
): { from: number; to: number } | null {
  const from = colIds.findIndex((colId) => colId.toLowerCase() === draggedId);
  if (from === -1) return null;
  const to = from < slot ? slot - 1 : slot;
  return to === from ? null : { from, to };
}

/**
 * Lets a chip dragged out of a row-group menu (`dragGroup`) drop between the chips the toolbar
 * panel shows. The panel's own drag is AG Grid's mouse-driven one, so this listens for the HTML5
 * drag the menus use and draws the same insertion line the menus do.
 */
export function useRowGroupPanelDrop(gridApi: Ref<GridApi | null>, dragGroup: string) {
  let panel: HTMLElement | null = null;

  function clearIndicator() {
    panel?.querySelectorAll(CHIP_SELECTOR).forEach((chip) => chip.classList.remove(...INDICATOR_CLASSES));
  }

  /** The hovered gap: the chip to mark, its side, and the slot it stands for. */
  function hoveredSlot(event: DragEvent) {
    const chips = [...(panel?.querySelectorAll<HTMLElement>(CHIP_SELECTOR) ?? [])].filter(
      (chip) => !chip.classList.contains(OVERFLOWED_CHIP_CLASS),
    );
    const hovered = (event.target as Element | null)?.closest<HTMLElement>(CHIP_SELECTOR);
    const index = hovered ? chips.indexOf(hovered) : -1;
    if (!hovered || index === -1) {
      const last = chips.at(-1);
      return last ? { chip: last, before: false, slot: chips.length } : null;
    }
    const rect = hovered.getBoundingClientRect();
    const before = event.clientX < rect.left + rect.width / 2;
    return { chip: hovered, before, slot: before ? index : index + 1 };
  }

  function planned(event: DragEvent) {
    const api = gridApi.value;
    const draggedId = draggedIdFromTypes(event);
    const hovered = hoveredSlot(event);
    if (!api || api.isDestroyed() || !draggedId || !hovered) return null;
    const colIds = api.getRowGroupColumns().map((col) => col.getColId());
    const move = rowGroupDropMove(colIds, draggedId, hovered.slot);
    return move && { ...move, ...hovered, api };
  }

  function onDragover(event: DragEvent) {
    if (!isOverChips(event.target) || !isFromDragGroup(event, dragGroup)) return;
    event.preventDefault();
    const plan = planned(event);
    if (event.dataTransfer) event.dataTransfer.dropEffect = plan ? "move" : "none";
    clearIndicator();
    plan?.chip.classList.add(plan.before ? dropIndicatorStyles.indicatorBefore : dropIndicatorStyles.indicatorAfter);
  }

  function onDragleave(event: DragEvent) {
    if (!isOverChips(event.relatedTarget)) clearIndicator();
  }

  function onDrop(event: DragEvent) {
    if (!isOverChips(event.target) || !isFromDragGroup(event, dragGroup)) return;
    event.preventDefault();
    const plan = planned(event);
    clearIndicator();
    plan?.api.moveRowGroupColumn(plan.from, plan.to);
  }

  function attach(el: HTMLElement) {
    detach();
    panel = el;
    panel.addEventListener("dragover", onDragover);
    panel.addEventListener("dragleave", onDragleave);
    panel.addEventListener("drop", onDrop);
  }

  function detach() {
    clearIndicator();
    panel?.removeEventListener("dragover", onDragover);
    panel?.removeEventListener("dragleave", onDragleave);
    panel?.removeEventListener("drop", onDrop);
    panel = null;
  }

  return { attach, detach };
}
